import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { type HelpTermId, TERMS } from "@/lib/help";

/**
 * An inline term that explains itself, used only where being wrong costs
 * something.
 *
 * Deliberately a real `<button>` opening a panel on click, not a hover
 * tooltip. Hover help is unreachable by keyboard, invisible on touch, and
 * disappears the moment you move the pointer towards it — which is exactly
 * when someone is trying to read it. The `?` affordance is a dotted underline
 * on the term itself rather than a separate icon, so the text stays readable
 * and the help sits on the word it describes.
 *
 * The panel is rendered after the trigger and referenced by `aria-controls`, so
 * a screen reader and the tab order both reach the explanation immediately
 * after the term. Escape closes it and returns focus, and every panel ends with
 * a link into the full page, so the inline answer is a door into the long one
 * rather than a dead end.
 */
export function HelpTerm({ id, children }: { id: HelpTermId; children?: React.ReactNode }) {
  const term = TERMS[id];
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        className="decoration-fg-muted hover:text-fg cursor-pointer underline decoration-dotted underline-offset-2"
      >
        {children ?? term.title}
      </button>
      {open ? (
        <span
          id={panelId}
          role="note"
          className="border-border bg-bg absolute top-full left-0 z-20 mt-1 block w-80 max-w-[calc(100vw-2rem)] rounded border p-3 shadow-lg"
        >
          <span className="text-fg block text-sm font-semibold">{term.title}</span>
          <span className="text-fg-muted mt-1 block text-sm">{term.body}</span>
          {term.caution ? (
            <span className="border-warning-border text-fg-muted mt-2 block border-l-2 pl-2 text-xs">
              {term.caution}
            </span>
          ) : null}
          <Link
            to={`/help/glossary#term-${id}`}
            className="text-fg-muted hover:text-fg mt-2 inline-block text-xs underline"
            onClick={() => setOpen(false)}
          >
            How offboarding works, end to end
          </Link>
        </span>
      ) : null}
    </span>
  );
}
